/** Objectives — list view (server-side pagination/sort/search + delete). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useGo, useNavigation } from "@refinedev/core";
import type { Objective } from "@aos/shared";

import { StatusBadge } from "@/components/app/StatusBadge";
import { RefLink } from "@/components/inspector";
import { formatDate } from "@/lib/format";

import { ResourceListPage, RowActions } from "../_shared/ListScaffold";

/**
 * ObjectiveList (D6) — OKR objectives on the shared list grammar. The first
 * column is the meaningful title (the row links to the detail page); period,
 * status (via StatusBadge), owner (via RefLink, never a raw id) and the created
 * date follow. Loading / empty / error states come from the shared DataTable.
 */
export function ObjectiveList() {
  const go = useGo();
  const { editUrl, showUrl } = useNavigation();
  const [pendingDelete, setPendingDelete] = React.useState<Objective | null>(
    null,
  );

  const columns = useMemo<ColumnDef<Objective, unknown>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.title}</span>
        ),
      },
      {
        id: "period",
        accessorKey: "period",
        header: "Period",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.period ?? "—"}
          </span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "owner_user_id",
        accessorKey: "owner_user_id",
        header: "Owner",
        enableSorting: false,
        cell: ({ row }) => (
          <RefLink resource="users" id={row.original.owner_user_id} />
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("objectives", row.original.id) })}
            onEdit={() => go({ to: editUrl("objectives", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, editUrl, showUrl],
  );

  return (
    <ResourceListPage<Objective>
      resource="objectives"
      title="Objectives"
      description="The outcomes a project is steering toward, each tracked by its key results."
      createLabel="New Objective"
      columns={columns}
      searchPlaceholder="Search objectives…"
      emptyMessage="No objectives yet. Create one to start tracking outcomes."
      deleteEntityLabel="objective"
      deleteName={(o) => o.title}
      pendingDelete={pendingDelete}
      setPendingDelete={setPendingDelete}
    />
  );
}

export default ObjectiveList;
